import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LayoutGrid, BarChart3, Settings2, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MODULES, moduleFor } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";

const DOT = { amber: "bg-amber-400", emerald: "bg-emerald-400", sky: "bg-sky-400", rose: "bg-rose-400", violet: "bg-violet-400", teal: "bg-teal-400", orange: "bg-orange-400" };

// "Current Module ▼" — switch workspaces instantly, no re-login. Also carries the global links.
export default function ModuleSwitcher() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isOwner, canHR } = useAuth();
  const current = moduleFor(pathname);
  const visible = Object.values(MODULES).filter((m) => !m.requires || (m.requires === "hr" && canHR));
  const global = pathname === "/dashboard" || pathname === "/reports";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="module-switcher" className="h-9 pl-2.5 pr-2 rounded-lg border border-slate-700 bg-slate-900/70 hover:bg-slate-800 text-xs font-semibold text-slate-200 inline-flex items-center gap-2 max-w-[200px]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${global ? "bg-slate-400" : DOT[current.tone]}`} />
          <span className="truncate"><span className="hidden lg:inline text-slate-500 font-normal">Current Module: </span>{global ? "Dashboard" : current.label}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-[#111827] border-slate-700 text-slate-100">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Switch module</DropdownMenuLabel>
        {visible.map((m) => (
          <DropdownMenuItem key={m.key} onClick={() => navigate(m.home)} data-testid={`module-${m.key}`} className="gap-2 cursor-pointer focus:bg-slate-800">
            <span className={`w-2 h-2 rounded-full ${DOT[m.tone]}`} /><span className="flex-1">{m.label} <span className="font-zh text-xs text-slate-500">{m.zh}</span></span>{!global && current.key === m.key && <Check className="w-4 h-4 text-amber-300" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-slate-800" />
        <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="module-dashboard" className="gap-2 cursor-pointer focus:bg-slate-800"><LayoutGrid className="w-4 h-4 text-slate-400" /> Dashboard</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/reports")} data-testid="module-reports" className="gap-2 cursor-pointer focus:bg-slate-800"><BarChart3 className="w-4 h-4 text-slate-400" /> Reports</DropdownMenuItem>
        {isOwner && <DropdownMenuItem onClick={() => navigate("/quote?settings=1")} data-testid="module-settings" className="gap-2 cursor-pointer focus:bg-slate-800"><Settings2 className="w-4 h-4 text-slate-400" /> Settings (Prices · Team · Backups)</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
